import { farShared } from "../../../../assets/jss/user/far/farTokens.jsx";

/** Address Book modal styling. Reuses the FAR tokens so light and dark themes match the Send page. */
const addressBookStyle = (theme) => {
    const s = farShared(theme);
    const t = s.t;
    const input = {
        height: "40px", padding: "0 12px", borderRadius: "8px", border: "1px solid "+t.border, boxSizing: "border-box",
        backgroundColor: t.inputBg, color: t.text, fontSize: "14px", outline: "none",
        "&:focus": { borderColor: t.focus, boxShadow: "0 0 0 2px "+t.focus+"33" }
    };
    const button = {
        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "6px",
        minHeight: "40px", padding: "0 18px", borderRadius: "8px", fontSize: "14px", fontWeight: 600, cursor: "pointer",
        "&:focus-visible": { outline: "2px solid "+t.focus, outlineOffset: "2px" },
        "&:disabled": { backgroundColor: t.disabledBg, color: t.disabledText, borderColor: t.disabledBg, cursor: "not-allowed" }
    };
    return {
        paper: {
            backgroundColor: t.dark ? "#121829" : "#FFFFFF", backgroundImage: "none", color: t.text,
            borderRadius: "14px", border: "1px solid "+t.border, width: "100%", maxWidth: "980px",
            margin: "16px", textAlign: "left", maxHeight: "calc(100% - 32px)"
        },
        header: { display: "flex", alignItems: "center", padding: "18px 24px", borderBottom: "1px solid "+t.border },
        title: { margin: 0, fontSize: "20px", fontWeight: 700, color: t.textStrong },
        iconButton: {
            background: "none", border: "none", color: t.textMuted, cursor: "pointer", padding: "6px", borderRadius: "6px",
            display: "inline-flex", "&:hover": { backgroundColor: t.surfaceHover, color: t.textStrong },
            "&:focus-visible": { outline: "2px solid "+t.focus }
        },
        close: { marginLeft: "auto" },
        layout: { display: "flex", minHeight: "440px", flex: 1, overflow: "hidden", "@media (max-width: 720px)": { flexDirection: "column" } },
        nav: {
            width: "220px", flexShrink: 0, padding: "16px 12px", borderRight: "1px solid "+t.border,
            display: "flex", flexDirection: "column", gap: "4px",
            "@media (max-width: 720px)": { width: "auto", flexDirection: "row", overflowX: "auto", borderRight: "none", borderBottom: "1px solid "+t.border, padding: "10px" }
        },
        navItem: {
            display: "flex", alignItems: "center", gap: "10px", padding: "10px 12px", borderRadius: "8px", border: "none",
            background: "none", color: t.text, fontSize: "14px", textAlign: "left", cursor: "pointer", whiteSpace: "nowrap",
            "& svg": { fontSize: "20px" },
            "& .sl-yahoo-nav-mark": {
                display: "inline-flex", alignItems: "center", justifyContent: "center", width: "20px", height: "20px", borderRadius: "4px",
                backgroundColor: "#6001D2", color: "#FFFFFF", fontSize: "10px", fontWeight: 800, letterSpacing: "-0.5px", flexShrink: 0
            },
            "&:hover": { backgroundColor: t.surfaceHover },
            "&:focus-visible": { outline: "2px solid "+t.focus }
        },
        navActive: { backgroundColor: t.accent+" !important", color: t.accentText+" !important" },
        navActiveYahoo: { backgroundColor: "#6001D2 !important", color: "#FFFFFF !important", "& .sl-yahoo-nav-mark": { backgroundColor: "#FFFFFF", color: "#6001D2" } },
        navDivider: { height: "1px", backgroundColor: t.border, margin: "8px 4px", "@media (max-width: 720px)": { display: "none" } },
        main: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", padding: "16px 20px", gap: "12px", overflow: "auto" },
        toolbar: { display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "center" },
        search: { ...input, flex: "1 1 220px", minWidth: 0 },
        select: { ...input, paddingRight: "8px" },
        input: { ...input, width: "100%" },
        label: { fontSize: "13px", fontWeight: 600, color: t.textStrong, marginBottom: "4px", display: "block" },
        tableWrap: { border: "1px solid "+t.border, borderRadius: "10px", overflow: "auto", maxHeight: "360px", flexShrink: 0 },
        table: {
            width: "100%", borderCollapse: "collapse", fontSize: "14px",
            "& th": {
                position: "sticky", top: 0, zIndex: 1, backgroundColor: t.surfaceAlt, color: t.textStrong, fontWeight: 600,
                textAlign: "left", padding: "10px 12px", borderBottom: "1px solid "+t.border, whiteSpace: "nowrap"
            },
            "& td": { padding: "8px 12px", borderBottom: "1px solid "+t.border, color: t.text, verticalAlign: "middle" },
            "& tr:last-child td": { borderBottom: "none" },
            "& tbody tr:hover td": { backgroundColor: t.surfaceHover }
        },
        rowSelected: { "& td": { backgroundColor: t.info.bg } },
        sortButton: { background: "none", border: "none", color: "inherit", font: "inherit", cursor: "pointer", padding: 0, display: "inline-flex", alignItems: "center", gap: "4px" },
        checkbox: { width: "18px", height: "18px", accentColor: t.accent, cursor: "pointer" },
        nameCell: { display: "flex", alignItems: "center", gap: "10px" },
        avatar: {
            width: "30px", height: "30px", borderRadius: "50%", flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center",
            backgroundColor: t.info.bg, color: t.info.fg, fontSize: "12px", fontWeight: 700
        },
        muted: { color: t.textMuted },
        star: { color: t.textMuted },
        starOn: { color: "#F5A623 !important" },
        empty: { padding: "32px 12px", textAlign: "center", color: t.textMuted },
        groupRow: { cursor: "pointer" },
        memberRow: { "& td:first-child": { paddingLeft: "36px" } },
        footer: {
            display: "flex", flexWrap: "wrap", alignItems: "center", gap: "10px", padding: "14px 24px",
            borderTop: "1px solid "+t.border
        },
        chips: { display: "flex", flexWrap: "wrap", gap: "6px", flex: "1 1 auto", minWidth: 0, maxHeight: "72px", overflow: "auto" },
        chip: {
            display: "inline-flex", alignItems: "center", gap: "4px", padding: "4px 6px 4px 12px", borderRadius: "999px",
            backgroundColor: t.info.bg, color: t.info.fg, border: "1px solid "+t.info.border, fontSize: "13px"
        },
        chipRemove: { background: "none", border: "none", color: "inherit", cursor: "pointer", display: "inline-flex", padding: "2px", "& svg": { fontSize: "16px" } },
        count: { color: t.textMuted, fontSize: "14px", whiteSpace: "nowrap" },
        primary: { ...button, border: "1px solid "+t.accent, backgroundColor: t.accent, color: t.accentText },
        secondary: { ...button, border: "1px solid "+t.borderStrong, backgroundColor: "transparent", color: t.textStrong },
        danger: { ...button, border: "1px solid "+t.danger.solid, backgroundColor: t.danger.solid, color: t.danger.onSolid },
        linkButton: { ...button, minHeight: "32px", padding: "0 10px", border: "none", background: "none", color: t.link },
        error: { color: t.danger.fg, backgroundColor: t.danger.bg, border: "1px solid "+t.danger.border, borderRadius: "8px", padding: "8px 12px", fontSize: "14px" },
        notice: { color: t.info.fg, backgroundColor: t.info.bg, border: "1px solid "+t.info.border, borderRadius: "8px", padding: "8px 12px", fontSize: "14px" },
        success: { color: t.success.fg, backgroundColor: t.success.bg, border: "1px solid "+t.success.border, borderRadius: "8px", padding: "8px 12px", fontSize: "14px" },
        dropzone: {
            border: "2px dashed "+t.borderStrong, borderRadius: "12px", padding: "28px 16px", textAlign: "center",
            display: "flex", flexDirection: "column", alignItems: "center", gap: "10px", color: t.text
        },
        dropActive: { borderColor: t.accent, backgroundColor: t.info.bg },
        tabs: { display: "flex", gap: "4px", borderBottom: "1px solid "+t.border },
        tab: {
            background: "none", border: "none", borderBottom: "2px solid transparent", padding: "10px 14px", color: t.textMuted,
            fontSize: "14px", fontWeight: 600, cursor: "pointer", "&:focus-visible": { outline: "2px solid "+t.focus }
        },
        tabActive: { color: t.link, borderBottomColor: t.link },
        formGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", "@media (max-width: 600px)": { gridTemplateColumns: "1fr" } },
        status: { fontSize: "12px", fontWeight: 600, padding: "2px 8px", borderRadius: "999px", whiteSpace: "nowrap" },
        statusOk: { color: t.success.fg, backgroundColor: t.success.bg },
        statusBad: { color: t.danger.fg, backgroundColor: t.danger.bg },
        statusSkip: { color: t.warning.fg, backgroundColor: t.warning.bg },
        actions: { display: "flex", gap: "4px", justifyContent: "flex-end" },
        groupPick: { display: "flex", flexWrap: "wrap", gap: "8px" },
        groupPickItem: { display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "14px", color: t.text, cursor: "pointer" },
        srOnly: { position: "absolute", width: "1px", height: "1px", overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap" },

        /* Recent Recipients status + Review & Save Contacts */
        subLine: { display: "block", fontSize: "12px", color: t.textMuted },
        statusMuted: { color: t.textMuted, backgroundColor: t.surfaceAlt, border: "1px solid "+t.border },
        statusNew: { color: t.info.fg, backgroundColor: t.info.bg },
        subTitle: { margin: 0, fontSize: "17px", fontWeight: 700, color: t.textStrong, outline: "none" },
        emailCell: { fontWeight: 600, color: t.textStrong, minWidth: "230px", overflowWrap: "anywhere", marginBottom: "4px" },
        cellInput: { ...input, height: "34px", width: "100%", minWidth: "110px", padding: "0 10px" },
        inputInvalid: { borderColor: t.danger.solid+" !important", boxShadow: "0 0 0 1px "+t.danger.solid+" !important" },
        fieldError: { color: t.danger.fg, fontSize: "12px", marginTop: "4px", lineHeight: 1.4 },
        rowInvalid: { "& td": { backgroundColor: t.danger.bg+" !important" } },
        reviewOptions: {
            display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px",
            "@media (max-width: 720px)": { gridTemplateColumns: "1fr" }
        },
        fieldset: { border: "1px solid "+t.border, borderRadius: "10px", padding: "12px 14px", margin: 0, minWidth: 0 },
        hint: { margin: "0 0 8px", fontSize: "13px", color: t.textMuted },
        groupList: { display: "flex", flexDirection: "column", gap: "8px", maxHeight: "150px", overflow: "auto" },
        inlineLink: { background: "none", border: "none", padding: 0, color: t.link, font: "inherit", textDecoration: "underline", cursor: "pointer" },
        reviewSummary: { display: "flex", flexDirection: "column", gap: "2px", flex: "1 1 260px", minWidth: 0, fontSize: "14px", color: t.text },
        blockReason: { color: t.textMuted, fontSize: "13px" },
        // Microsoft 365 view
        msHeader: { display: "flex", alignItems: "center", gap: "12px", "& p": { margin: "2px 0 0", fontSize: "13px" } },
        msIcon: {
            display: "inline-flex", alignItems: "center", justifyContent: "center", width: "40px", height: "40px", flexShrink: 0,
            borderRadius: "10px", backgroundColor: t.info.bg, color: t.info.fg, border: "1px solid "+t.info.border
        },
        msTitle: { margin: 0, fontSize: "16px", fontWeight: 700, color: t.textStrong },
        msPanel: {
            border: "1px solid "+t.border, borderRadius: "10px", padding: "16px", backgroundColor: t.surfaceAlt,
            display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "10px", fontSize: "14px", color: t.text,
            "& p": { margin: 0 }
        },
        msList: { margin: 0, paddingLeft: "18px", color: t.textMuted, fontSize: "13px", lineHeight: 1.6, alignSelf: "stretch" },
        msConnectTitle: {
            display: "flex", alignItems: "center", gap: "10px", margin: 0, fontSize: "17px", fontWeight: 700, color: t.textStrong,
            "& svg": { color: t.link, fontSize: "24px" }
        },
        msLead: { color: t.text, fontSize: "14px" },
        msWaiting: {
            alignSelf: "stretch", display: "flex", alignItems: "center", flexWrap: "wrap", gap: "10px 14px",
            padding: "10px 12px", borderRadius: "10px", border: "1px solid "+t.info.border, backgroundColor: t.info.bg,
            color: t.info.fg, fontSize: "13px", "& span": { flex: "1 1 260px" }
        },
        msConnectButton: {
            ...button, alignSelf: "stretch", minHeight: "46px", borderRadius: "999px", fontSize: "15px",
            border: "1px solid "+t.border, backgroundColor: t.dark ? "#253047" : t.surface, color: t.textStrong,
            "& svg": { fontSize: "20px", transform: "rotate(-45deg)" },
            "&:hover:not(:disabled)": { backgroundColor: t.dark ? "#2E3A55" : t.surfaceHover, borderColor: t.link }
        },
        msWarn: { display: "flex", alignItems: "flex-start", gap: "8px", color: t.warning.fg, "& svg": { flexShrink: 0, fontSize: "20px" } },
        msConnected: {
            display: "flex", alignItems: "center", flexWrap: "wrap", gap: "10px", padding: "10px 12px", borderRadius: "10px",
            border: "1px solid "+t.success.border, backgroundColor: t.success.bg, fontSize: "14px", color: t.text,
            "& svg": { fontSize: "18px" }
        },
        msOk: { color: t.success.solid },
        msAccount: { flex: "1 1 220px", minWidth: 0, overflowWrap: "anywhere" },
        msConfirm: { display: "inline-flex", alignItems: "center", flexWrap: "wrap", gap: "8px" },
        msDisconnect: {
            ...button, minHeight: "34px", padding: "0 14px", border: "1px solid "+t.danger.solid, backgroundColor: "transparent", color: t.danger.fg,
            "&:hover": { backgroundColor: t.danger.bg }
        },
        // "More Address Books" (Address Book extension off)
        abMoreList: {
            listStyle: "none", margin: 0, padding: 0, alignSelf: "stretch", display: "grid", gap: "8px",
            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
            "& li": {
                display: "flex", alignItems: "center", gap: "10px", padding: "10px 12px", borderRadius: "8px",
                border: "1px solid "+t.border, backgroundColor: t.dark ? "rgba(255,255,255,0.03)" : t.surface, color: t.textStrong, fontSize: "14px"
            },
            "& li svg": { fontSize: "20px", color: t.link, flexShrink: 0 },
            "& li .sl-yahoo-nav-mark": {
                display: "inline-flex", alignItems: "center", justifyContent: "center", width: "20px", height: "20px", borderRadius: "4px",
                backgroundColor: "#6001D2", color: "#FFFFFF", fontSize: "10px", fontWeight: 800, letterSpacing: "-0.5px", flexShrink: 0
            }
        },
        abMoreButton: { "& svg": { transform: "none" } },
        // iCloud Contacts view
        icTile: {
            display: "inline-flex", alignItems: "center", justifyContent: "center", width: "48px", height: "48px", flexShrink: 0,
            borderRadius: "12px", background: "linear-gradient(180deg, #4FA8FF 0%, #1E7BEF 100%)", color: "#FFFFFF",
            "& svg": { fontSize: "30px" }
        },
        icTileSmall: {
            display: "inline-flex", alignItems: "center", justifyContent: "center", width: "40px", height: "40px", flexShrink: 0,
            borderRadius: "10px", backgroundColor: t.dark ? "#1D3A66" : t.info.bg, color: t.dark ? "#FFFFFF" : t.info.fg,
            "& svg": { fontSize: "24px" }
        },
        icPanel: {
            border: "1px solid "+t.border, borderRadius: "12px", padding: "18px", backgroundColor: t.surfaceAlt,
            fontSize: "14px", color: t.text, "& p": { margin: 0 }
        },
        icForm: { display: "flex", flexDirection: "column", gap: "14px" },
        icIntro: { display: "flex", alignItems: "flex-start", gap: "12px" },
        icConnectTitle: { margin: "0 0 2px", fontSize: "17px", fontWeight: 700, color: t.textStrong },
        icLead: { color: t.textMuted, fontSize: "14px" },
        icField: { display: "flex", flexDirection: "column" },
        icLabelRow: { display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px" },
        icInfoButton: {
            background: "none", border: "none", padding: "2px", borderRadius: "50%", color: t.textMuted, cursor: "pointer", display: "inline-flex",
            "& svg": { fontSize: "18px" }, "&:hover": { color: t.textStrong }, "&:focus-visible": { outline: "2px solid "+t.focus }
        },
        icPasswordWrap: { position: "relative", display: "flex" },
        icPasswordInput: { paddingRight: "44px !important" },
        icEye: {
            position: "absolute", right: "4px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none",
            color: t.textMuted, cursor: "pointer", padding: "6px", borderRadius: "6px", display: "inline-flex",
            "& svg": { fontSize: "20px" }, "&:hover": { color: t.textStrong }, "&:focus-visible": { outline: "2px solid "+t.focus }
        },
        icConnectButton: {
            ...button, width: "100%", minHeight: "46px", fontSize: "15px", border: "1px solid "+t.accent, backgroundColor: t.accent, color: t.accentText,
            "& svg": { fontSize: "20px", transform: "rotate(-45deg)" },
            "&:hover:not(:disabled)": { filter: "brightness(1.08)" }
        },
        icHelp: {
            display: "grid", gridTemplateColumns: "minmax(0, 1fr) 220px", gap: "16px", padding: "14px 16px", borderRadius: "10px",
            border: "1px solid "+t.border, backgroundColor: t.dark ? "#141B2E" : t.surface,
            "@media (max-width: 720px)": { gridTemplateColumns: "1fr" }
        },
        icHelpSteps: { display: "flex", gap: "12px", alignItems: "flex-start", minWidth: 0 },
        icBulb: { color: "#5AB0FF", fontSize: "26px !important", flexShrink: 0 },
        icHelpTitle: { margin: "0 0 4px !important", fontWeight: 700, color: t.textStrong, fontSize: "14px" },
        icSteps: { margin: 0, paddingLeft: "20px", color: t.text, fontSize: "13px", lineHeight: 1.7 },
        icHelpLink: {
            display: "flex", flexDirection: "column", gap: "4px", justifyContent: "center", paddingLeft: "16px", borderLeft: "1px solid "+t.border, fontSize: "13px",
            "@media (max-width: 720px)": { paddingLeft: 0, borderLeft: "none", paddingTop: "12px", borderTop: "1px solid "+t.border }
        },
        // Yahoo Contacts view (Yahoo purple accent)
        yhTile: {
            display: "inline-flex", alignItems: "center", justifyContent: "center", width: "48px", height: "48px", flexShrink: 0,
            borderRadius: "12px", backgroundColor: "#6001D2", color: "#FFFFFF", fontSize: "22px", fontWeight: 800, letterSpacing: "-1px",
            fontFamily: "Arial, Helvetica, sans-serif"
        },
        yhTileSmall: {
            display: "inline-flex", alignItems: "center", justifyContent: "center", width: "34px", height: "34px", flexShrink: 0,
            borderRadius: "8px", backgroundColor: t.dark ? "#3B1A7A" : "#EFE6FD", color: t.dark ? "#FFFFFF" : "#5B21B6", "& svg": { fontSize: "22px" }
        },
        yhPanel: {
            border: "1px solid "+t.border, borderRadius: "12px", padding: "18px", backgroundColor: t.surfaceAlt,
            display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "12px", fontSize: "14px", color: t.text,
            "& p": { margin: 0 }, "& > div": { alignSelf: "stretch" }
        },
        yhConnectTitle: { display: "flex", alignItems: "center", gap: "12px", margin: 0, fontSize: "17px", fontWeight: 700, color: t.textStrong },
        yhConnectButton: {
            ...button, alignSelf: "stretch", minHeight: "48px", borderRadius: "999px", fontSize: "15px",
            border: "1px solid #6001D2", backgroundColor: "#6001D2", color: "#FFFFFF",
            "& svg": { fontSize: "20px", transform: "rotate(-45deg)" },
            "&:hover:not(:disabled)": { backgroundColor: "#7B2BE6", borderColor: "#7B2BE6" }
        },
        icAppleLink: {
            display: "inline-flex", alignItems: "center", gap: "8px", color: t.link+" !important", fontWeight: 700, fontSize: "15px", textDecoration: "none",
            "& svg": { fontSize: "22px" }, "&:hover": { textDecoration: "underline" }, "&:focus-visible": { outline: "2px solid "+t.focus, outlineOffset: "2px" }
        }
    };
};

export default addressBookStyle;
